/**
 * mrpFormatters.ts
 *
 * Vietnamese labels, tones, and formatting helpers for MRP Planning UI.
 */

import type { BadgeTone } from '@/components/ui/StatusBadge'
import type { MrpIssueCode, StructuredMaterialSpec } from '../types/mrpTypes'

export function formatSpecDisplay(
  spec?: StructuredMaterialSpec | string | null,
): string {
  if (!spec) return '—'
  if (typeof spec === 'string') {
    const trimmed = spec.trim()
    return trimmed ? trimmed : '—'
  }

  const parts: string[] = []
  const { thick, width, length, height, kt1, kt2, kt3, density, packagingKind, quality, spec_text } = spec

  if (spec_text && typeof spec_text === 'string' && spec_text.trim()) {
    parts.push(spec_text.trim())
  }

  // 1. Dimensions: standard (thick, width, length, height)
  const dims: (number | string)[] = []
  if (thick != null) dims.push(thick)
  if (width != null) dims.push(width)
  if (length != null) dims.push(length)
  if (height != null) dims.push(`H${height}`)

  if (dims.length > 0) {
    parts.push(dims.join(' × '))
  }

  // 2. Dimensions: H1 / packaging / foam (kt1, kt2, kt3)
  const ktDims: (number | string)[] = []
  if (kt1 != null && kt1 !== '') ktDims.push(kt1)
  if (kt2 != null && kt2 !== '') ktDims.push(kt2)
  if (kt3 != null && kt3 !== '') ktDims.push(kt3)

  if (ktDims.length > 0) {
    const ktStr = ktDims.join(' × ')
    if (!parts.includes(ktStr)) {
      parts.push(ktStr)
    }
  }

  // 3. Packaging kind (loại đóng gói / bao bì)
  if (packagingKind != null && String(packagingKind).trim() !== '') {
    parts.push(String(packagingKind).trim())
  }

  // 4. Density / Định lượng
  if (density != null && String(density).trim() !== '') {
    const dStr = String(density).trim()
    parts.push(/kg|d/i.test(dStr) ? dStr : `Tỷ trọng ${dStr}`)
  }

  // 5. Quality / Phân loại
  if (quality && typeof quality === 'string' && quality.trim()) {
    parts.push(`Loại ${quality.trim()}`)
  }

  // 6. Nested attributes object (if any)
  if (spec.attributes && typeof spec.attributes === 'object') {
    const attrEntries = Object.entries(spec.attributes)
      .filter(([_, v]) => v != null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}: ${v}`)
    if (attrEntries.length > 0) {
      parts.push(attrEntries.join(', '))
    }
  }

  // 7. Deterministic generic fallback for other unknown normalized top-level fields
  const handledKeys = new Set([
    'thick',
    'width',
    'length',
    'height',
    'kt1',
    'kt2',
    'kt3',
    'density',
    'packagingKind',
    'quality',
    'spec_text',
    'attributes',
    'raw',
  ])

  const extraKeys = Object.keys(spec)
    .filter((k) => !handledKeys.has(k))
    .sort((a, b) => a.localeCompare(b))

  for (const k of extraKeys) {
    const val = spec[k]
    if (val == null || val === '') continue
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      parts.push(`${k}: ${val}`)
    } else if (Array.isArray(val) && val.length > 0) {
      parts.push(`${k}: [${val.filter((x) => x != null).join(', ')}]`)
    } else if (typeof val === 'object') {
      const subKeys = Object.keys(val as object).filter((sk) => (val as any)[sk] != null).sort()
      if (subKeys.length > 0) {
        parts.push(`${k}: {${subKeys.map((sk) => `${sk}: ${(val as any)[sk]}`).join(', ')}}`)
      }
    }
  }

  return parts.length > 0 ? parts.join(' · ') : '—'
}

export function formatIssueInfo(issue: MrpIssueCode): { label: string; tone: BadgeTone } {
  switch (issue) {
    case 'MATERIAL_UNRESOLVED':
      return { label: 'Chưa gán mã vật tư', tone: 'danger' }
    case 'BASE_UOM_MISSING':
      return { label: 'Thiếu ĐVT cơ sở', tone: 'danger' }
    case 'UOM_CONVERSION_REQUIRED':
      return { label: 'Cần quy đổi ĐVT', tone: 'warning' }
    case 'INCOMING_UOM_UNRESOLVED':
      return { label: 'PO thiếu quy đổi ĐVT', tone: 'warning' }
    case 'INVENTORY_INTEGRITY_VIOLATION':
      return { label: 'Lỗi toàn vẹn tồn kho', tone: 'danger' }
    default:
      return { label: 'Cảnh báo khác', tone: 'warning' }
  }
}

export function formatDemandTypeLabel(dt: string): string {
  switch (dt?.toUpperCase()) {
    case 'PRODUCTION':
      return 'Lệnh sản xuất'
    case 'PURCHASING':
      return 'Mua hàng ngoài'
    case 'PRODUCTION_MAKEUP':
      return 'Bổ sung sản xuất'
    case 'ALL':
      return 'Tất cả nguồn'
    default:
      return dt || 'Không xác định'
  }
}

export function formatNumberVi(val: number | null | undefined): string {
  if (val == null || !Number.isFinite(val)) return '0'
  return new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 4,
  }).format(val)
}
